import React, { useState, useEffect, useMemo } from "react";
import { Alert, Box, Tab, Tabs, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ApprovalInboxTab from "./components/ApprovalInboxTab";
import ApprovalPoliciesTab from "./components/ApprovalPoliciesTab";
import AuditLogTab from "./components/AuditLogTab";
import { INBOX_VIEWS } from "./constants";

const TABS = [
  { value: "inbox", label: "Pending approvals", roles: ["admin", "manager", "relationship_manager"] },
  { value: "policies", label: "Approval settings", roles: ["admin", "manager"] },
  { value: "audit", label: "Audit log", roles: ["admin", "manager", "relationship_manager"] },
];

const AuditApprovals = () => {
  const roleFromState = useSelector((state) => state.role);
  const isAdminFlag = useSelector((state) => state.isAdmin);
  const role = roleFromState || (isAdminFlag === 1 ? "admin" : "manager");

  const visibleTabs = useMemo(
    () => TABS.filter((tab) => tab.roles.includes(role)),
    [role]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const viewFromUrl = searchParams.get("view");
  const resolvedTabFromUrl =
    tabFromUrl && visibleTabs.some((t) => t.value === tabFromUrl)
      ? tabFromUrl
      : null;
  const [activeTab, setActiveTab] = useState(
    () => resolvedTabFromUrl ?? visibleTabs[0]?.value ?? "inbox"
  );
  const inboxInitialView = INBOX_VIEWS.some((v) => v.value === viewFromUrl)
    ? viewFromUrl
    : "pending_for_me";

  // Sync tab only when the URL changes (e.g. notification deep-link), not on every render.
  useEffect(() => {
    if (resolvedTabFromUrl) {
      setActiveTab(resolvedTabFromUrl);
    }
  }, [resolvedTabFromUrl]);

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      if (value !== "inbox") {
        next.delete("view");
      }
      return next;
    });
  };

  if (visibleTabs.length === 0) {
    return (
      <Box m={2}>
        <Alert severity="warning">You do not have access to audit or approvals.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" sx={{ mb: 1 }}>
        Audit &amp; Approvals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review pending maker-checker requests, configure approval policies, and browse the activity audit trail.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {visibleTabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {activeTab === "inbox" && <ApprovalInboxTab initialView={inboxInitialView} />}
      {activeTab === "policies" && <ApprovalPoliciesTab />}
      {activeTab === "audit" && <AuditLogTab />}
    </Box>
  );
};

export default AuditApprovals;
